import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import * as Icons from "lucide-react";
import { Link } from "react-router-dom";

export const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always enabled
    statistics: false,
    marketing: false,
    externalMedia: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      setShowBanner(true);
    } else {
      try {
        const savedPreferences = JSON.parse(consent);
        setPreferences(savedPreferences);
        applyConsent(savedPreferences);
      } catch (e) {
        // Corrupt/legacy value — reset and show banner again
        localStorage.removeItem("cookieConsent");
        setShowBanner(true);
      }
    }
  }, []);

  const applyConsent = (prefs) => {
    // Apply cookie settings based on consent
    if (prefs.statistics) {
      // Enable analytics cookies
      console.log("Statistics cookies enabled");
    }
    if (prefs.marketing) {
      // Enable marketing cookies
      console.log("Marketing cookies enabled");
    }
    if (prefs.externalMedia) {
      // Enable external media
      console.log("External media cookies enabled");
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      statistics: true,
      marketing: true,
      externalMedia: true,
    };
    saveConsent(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyEssential = {
      essential: true,
      statistics: false,
      marketing: false,
      externalMedia: false,
    };
    saveConsent(onlyEssential);
  };

  const handleSaveSettings = () => {
    saveConsent(preferences);
  };

  const saveConsent = (prefs) => {
    localStorage.setItem("cookieConsent", JSON.stringify(prefs));
    setPreferences(prefs);
    applyConsent(prefs);
    setShowBanner(false);
    setShowSettings(false);
  };

  const togglePreference = (key) => {
    if (key === "essential") return; // Essential cannot be disabled
    setPreferences({ ...preferences, [key]: !preferences[key] });
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40" />

      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6">
        <Card className="max-w-5xl mx-auto shadow-2xl border-gray-200">
          <CardContent className="p-6 sm:p-8">
            {!showSettings ? (
              // Main Banner
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <Icons.Cookie className="text-[#1f7db6]" size={32} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#143e6e] mb-2">
                      Cookie-Einstellungen
                    </h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Wir nutzen Cookies, um Ihnen die bestmögliche Nutzung unserer Website zu ermöglichen. 
                      Einige Cookies sind für den Betrieb der Website technisch notwendig. Andere Cookies helfen 
                      uns, die Website und Ihre Erfahrung zu verbessern.
                    </p>
                    <p className="text-sm text-gray-600">
                      Weitere Informationen finden Sie in unserer{" "}
                      <Link to="/datenschutz" className="text-[#1f7db6] underline hover:text-[#143e6e]">
                        Datenschutzerklärung
                      </Link>
                      .
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleAcceptAll}
                    className="flex-1 bg-[#1f7db6] hover:bg-[#143e6e] text-white font-medium"
                  >
                    <Icons.Check className="mr-2" size={18} />
                    Alle akzeptieren
                  </Button>
                  <Button
                    onClick={handleRejectAll}
                    variant="outline"
                    className="flex-1 border-gray-300 hover:bg-gray-50 font-medium"
                  >
                    <Icons.X className="mr-2" size={18} />
                    Alle ablehnen
                  </Button>
                  <Button
                    onClick={() => setShowSettings(true)}
                    variant="outline"
                    className="flex-1 border-[#1f7db6] text-[#1f7db6] hover:bg-[#1f7db6]/5 font-medium"
                  >
                    <Icons.Settings className="mr-2" size={18} />
                    Einstellungen
                  </Button>
                </div>
              </div>
            ) : (
              // Settings Panel
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-[#143e6e]">
                    Cookie-Einstellungen anpassen
                  </h3>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <Icons.X size={24} />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Essential */}
                  <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1 pr-4">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-[#143e6e]">Essenziell</h4>
                        <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                          Immer aktiv
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Diese Cookies sind für die Grundfunktionen der Website erforderlich.
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <div className="w-12 h-6 bg-[#1f7db6] rounded-full flex items-center justify-end px-1">
                        <div className="w-4 h-4 bg-white rounded-full" />
                      </div>
                    </div>
                  </div>

                  {/* Statistics */}
                  <div className="flex items-start justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex-1 pr-4">
                      <h4 className="font-semibold text-[#143e6e] mb-1">Statistik</h4>
                      <p className="text-sm text-gray-600">
                        Statistik-Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren.
                      </p>
                    </div>
                    <button
                      onClick={() => togglePreference("statistics")}
                      className="flex-shrink-0"
                    >
                      <div
                        className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                          preferences.statistics ? "bg-[#1f7db6] justify-end" : "bg-gray-300 justify-start"
                        } px-1`}
                      >
                        <div className="w-4 h-4 bg-white rounded-full" />
                      </div>
                    </button>
                  </div>

                  {/* Marketing */}
                  <div className="flex items-start justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex-1 pr-4">
                      <h4 className="font-semibold text-[#143e6e] mb-1">Marketing</h4>
                      <p className="text-sm text-gray-600">
                        Marketing-Cookies werden verwendet, um Besuchern relevante Anzeigen und Kampagnen anzuzeigen.
                      </p>
                    </div>
                    <button
                      onClick={() => togglePreference("marketing")}
                      className="flex-shrink-0"
                    >
                      <div
                        className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                          preferences.marketing ? "bg-[#1f7db6] justify-end" : "bg-gray-300 justify-start"
                        } px-1`}
                      >
                        <div className="w-4 h-4 bg-white rounded-full" />
                      </div>
                    </button>
                  </div>

                  {/* External Media */}
                  <div className="flex items-start justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex-1 pr-4">
                      <h4 className="font-semibold text-[#143e6e] mb-1">Externe Medien</h4>
                      <p className="text-sm text-gray-600">
                        Externe Medien-Cookies werden verwendet, um Inhalte von Drittanbietern anzuzeigen.
                      </p>
                    </div>
                    <button
                      onClick={() => togglePreference("externalMedia")}
                      className="flex-shrink-0"
                    >
                      <div
                        className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                          preferences.externalMedia ? "bg-[#1f7db6] justify-end" : "bg-gray-300 justify-start"
                        } px-1`}
                      >
                        <div className="w-4 h-4 bg-white rounded-full" />
                      </div>
                    </button>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button
                    onClick={handleSaveSettings}
                    className="flex-1 bg-[#1f7db6] hover:bg-[#143e6e] text-white font-medium"
                  >
                    <Icons.Save className="mr-2" size={18} />
                    Auswahl speichern
                  </Button>
                  <Button
                    onClick={handleAcceptAll}
                    variant="outline"
                    className="flex-1 border-[#1f7db6] text-[#1f7db6] hover:bg-[#1f7db6]/5 font-medium"
                  >
                    Alle akzeptieren
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

// Component to reopen cookie settings
export const CookieSettingsLink = ({ className = "" }) => {
  const reopenCookieSettings = () => {
    localStorage.removeItem("cookieConsent");
    window.location.reload();
  };

  return (
    <button
      onClick={reopenCookieSettings}
      className={`text-[#1f7db6] hover:text-[#143e6e] transition-colors ${className}`}
    >
      Cookie-Einstellungen
    </button>
  );
};
